import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import Layout from './components/layout/Layout';

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-sm text-gray-500">Chargement...</p>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-lg text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/complete-profile',
    lazy: async () => {
      const { default: CompleteProfile } = await import('./pages/CompleteProfile');
      return { Component: CompleteProfile };
    },
  },
  {
    path: '/reset-password',
    lazy: async () => {
      const { default: ResetPassword } = await import('./pages/ResetPassword');
      return { Component: ResetPassword };
    },
  },
  {
    path: '/auth/verify-magic',
    lazy: async () => {
      const { default: VerifyMagicLink } = await import('./pages/VerifyMagicLink');
      return { Component: VerifyMagicLink };
    },
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'technician',
        lazy: async () => {
          const { default: TechnicianDashboard } = await import('./pages/TechnicianDashboard');
          return { Component: TechnicianDashboard };
        },
      },
      {
        path: 'scan',
        lazy: async () => {
          const { default: QRScanner } = await import('./pages/QRScanner');
          return { Component: QRScanner };
        },
      },
      {
        path: 'equipment',
        lazy: async () => {
          const { default: EquipmentList } = await import('./pages/EquipmentList');
          return { Component: EquipmentList };
        },
      },
      {
        path: 'equipment/new',
        lazy: async () => {
          const { default: EquipmentForm } = await import('./pages/EquipmentForm');
          return { Component: EquipmentForm };
        },
      },
      {
        path: 'equipment/:id/edit',
        lazy: async () => {
          const { default: EquipmentForm } = await import('./pages/EquipmentForm');
          return { Component: EquipmentForm };
        },
      },
      {
        path: 'equipment/:id',
        lazy: async () => {
          const { default: EquipmentDetail } = await import('./pages/EquipmentDetail');
          return { Component: EquipmentDetail };
        },
      },
      {
        path: 'work-orders',
        lazy: async () => {
          const { default: WorkOrdersList } = await import('./pages/WorkOrdersList');
          return { Component: WorkOrdersList };
        },
      },
      {
        path: 'work-orders/new',
        lazy: async () => {
          const { default: WorkOrderForm } = await import('./pages/WorkOrderForm');
          return { Component: WorkOrderForm };
        },
      },
      {
        path: 'work-orders/:id/edit',
        lazy: async () => {
          const { default: WorkOrderForm } = await import('./pages/WorkOrderForm');
          return { Component: WorkOrderForm };
        },
      },
      {
        path: 'work-orders/:id',
        lazy: async () => {
          const { default: WorkOrderDetail } = await import('./pages/WorkOrderDetail');
          return { Component: WorkOrderDetail };
        },
      },
      {
        path: 'work-requests',
        lazy: async () => {
          const { default: WorkRequestsList } = await import('./pages/WorkRequestsList');
          return { Component: WorkRequestsList };
        },
      },
      {
        path: 'work-requests/new',
        lazy: async () => {
          const { default: WorkRequestForm } = await import('./pages/WorkRequestForm');
          return { Component: WorkRequestForm };
        },
      },
      {
        path: 'work-requests/:id/edit',
        lazy: async () => {
          const { default: WorkRequestForm } = await import('./pages/WorkRequestForm');
          return { Component: WorkRequestForm };
        },
      },
      {
        path: 'work-requests/:id',
        lazy: async () => {
          const { default: WorkRequestDetail } = await import('./pages/WorkRequestDetail');
          return { Component: WorkRequestDetail };
        },
      },
      {
        path: 'preventive-plans',
        lazy: async () => {
          const { default: PreventivePlansList } = await import('./pages/PreventivePlansList');
          return { Component: PreventivePlansList };
        },
      },
      {
        path: 'preventive-plans/new',
        lazy: async () => {
          const { default: PreventivePlanForm } = await import('./pages/PreventivePlanForm');
          return { Component: PreventivePlanForm };
        },
      },
      {
        path: 'preventive-plans/:id/edit',
        lazy: async () => {
          const { default: PreventivePlanForm } = await import('./pages/PreventivePlanForm');
          return { Component: PreventivePlanForm };
        },
      },
      {
        path: 'preventive-plans/:id',
        lazy: async () => {
          const { default: PreventivePlanDetail } = await import('./pages/PreventivePlanDetail');
          return { Component: PreventivePlanDetail };
        },
      },
      {
        path: 'preventive-tasks',
        lazy: async () => {
          const { default: PreventiveTasksList } = await import('./pages/PreventiveTasksList');
          return { Component: PreventiveTasksList };
        },
      },
      {
        path: 'preventive-tasks/:id',
        lazy: async () => {
          const { default: PreventiveTaskDetail } = await import('./pages/PreventiveTaskDetail');
          return { Component: PreventiveTaskDetail };
        },
      },
      {
        path: 'articles',
        lazy: async () => {
          const { default: ArticlesList } = await import('./pages/ArticlesList');
          return { Component: ArticlesList };
        },
      },
      {
        path: 'articles/new',
        lazy: async () => {
          const { default: ArticleForm } = await import('./pages/ArticleForm');
          return { Component: ArticleForm };
        },
      },
      {
        path: 'articles/:id/edit',
        lazy: async () => {
          const { default: ArticleForm } = await import('./pages/ArticleForm');
          return { Component: ArticleForm };
        },
      },
      {
        path: 'articles/:id',
        lazy: async () => {
          const { default: ArticleDetail } = await import('./pages/ArticleDetail');
          return { Component: ArticleDetail };
        },
      },
      {
        path: 'purchase-requests',
        lazy: async () => {
          const { default: PurchaseRequestsList } = await import('./pages/PurchaseRequestsList');
          return { Component: PurchaseRequestsList };
        },
      },
      {
        path: 'purchase-requests/new',
        lazy: async () => {
          const { default: PurchaseRequestForm } = await import('./pages/PurchaseRequestForm');
          return { Component: PurchaseRequestForm };
        },
      },
      {
        path: 'purchase-requests/:id/edit',
        lazy: async () => {
          const { default: PurchaseRequestForm } = await import('./pages/PurchaseRequestForm');
          return { Component: PurchaseRequestForm };
        },
      },
      {
        path: 'purchase-requests/:id',
        lazy: async () => {
          const { default: PurchaseRequestDetail } = await import('./pages/PurchaseRequestDetail');
          return { Component: PurchaseRequestDetail };
        },
      },
      {
        path: 'purchase-orders',
        lazy: async () => {
          const { default: PurchaseOrdersList } = await import('./pages/PurchaseOrdersList');
          return { Component: PurchaseOrdersList };
        },
      },
      {
        path: 'purchase-orders/new',
        lazy: async () => {
          const { default: PurchaseOrderForm } = await import('./pages/PurchaseOrderForm');
          return { Component: PurchaseOrderForm };
        },
      },
      {
        path: 'purchase-orders/:id/edit',
        lazy: async () => {
          const { default: PurchaseOrderForm } = await import('./pages/PurchaseOrderForm');
          return { Component: PurchaseOrderForm };
        },
      },
      {
        path: 'purchase-orders/:id',
        lazy: async () => {
          const { default: PurchaseOrderDetail } = await import('./pages/PurchaseOrderDetail');
          return { Component: PurchaseOrderDetail };
        },
      },
      {
        path: 'contracts',
        lazy: async () => {
          const { default: ContractsList } = await import('./pages/ContractsList');
          return { Component: ContractsList };
        },
      },
      {
        path: 'contracts/new',
        lazy: async () => {
          const { default: ContractForm } = await import('./pages/ContractForm');
          return { Component: ContractForm };
        },
      },
      {
        path: 'contracts/:id/edit',
        lazy: async () => {
          const { default: ContractForm } = await import('./pages/ContractForm');
          return { Component: ContractForm };
        },
      },
      {
        path: 'contracts/:id',
        lazy: async () => {
          const { default: ContractDetail } = await import('./pages/ContractDetail');
          return { Component: ContractDetail };
        },
      },
      {
        path: 'budgets',
        lazy: async () => {
          const { default: BudgetsList } = await import('./pages/BudgetsList');
          return { Component: BudgetsList };
        },
      },
      {
        path: 'budgets/new',
        lazy: async () => {
          const { default: BudgetForm } = await import('./pages/BudgetForm');
          return { Component: BudgetForm };
        },
      },
      {
        path: 'budgets/:id/edit',
        lazy: async () => {
          const { default: BudgetForm } = await import('./pages/BudgetForm');
          return { Component: BudgetForm };
        },
      },
      {
        path: 'budgets/:id',
        lazy: async () => {
          const { default: BudgetDetail } = await import('./pages/BudgetDetail');
          return { Component: BudgetDetail };
        },
      },
      {
        path: 'projects',
        lazy: async () => {
          const { default: ProjectsList } = await import('./pages/ProjectsList');
          return { Component: ProjectsList };
        },
      },
      {
        path: 'projects/new',
        lazy: async () => {
          const { default: ProjectForm } = await import('./pages/ProjectForm');
          return { Component: ProjectForm };
        },
      },
      {
        path: 'projects/:id/edit',
        lazy: async () => {
          const { default: ProjectForm } = await import('./pages/ProjectForm');
          return { Component: ProjectForm };
        },
      },
      {
        path: 'projects/:id',
        lazy: async () => {
          const { default: ProjectDetail } = await import('./pages/ProjectDetail');
          return { Component: ProjectDetail };
        },
      },
      {
        path: 'roles',
        lazy: async () => {
          const { default: RolesList } = await import('./pages/RolesList');
          return { Component: RolesList };
        },
      },
      {
        path: 'roles/new',
        lazy: async () => {
          const { default: RoleForm } = await import('./pages/RoleForm');
          return { Component: RoleForm };
        },
      },
      {
        path: 'roles/:id/edit',
        lazy: async () => {
          const { default: RoleForm } = await import('./pages/RoleForm');
          return { Component: RoleForm };
        },
      },
      {
        path: 'roles/:id',
        lazy: async () => {
          const { default: RoleDetail } = await import('./pages/RoleDetail');
          return { Component: RoleDetail };
        },
      },
      {
        path: 'vehicles',
        lazy: async () => {
          const { default: VehiclesList } = await import('./pages/VehiclesList');
          return { Component: VehiclesList };
        },
      },
      {
        path: 'vehicles/new',
        lazy: async () => {
          const { default: VehicleForm } = await import('./pages/VehicleForm');
          return { Component: VehicleForm };
        },
      },
      {
        path: 'vehicles/:id/edit',
        lazy: async () => {
          const { default: VehicleForm } = await import('./pages/VehicleForm');
          return { Component: VehicleForm };
        },
      },
      {
        path: 'vehicles/:id',
        lazy: async () => {
          const { default: VehicleDetail } = await import('./pages/VehicleDetail');
          return { Component: VehicleDetail };
        },
      },
      {
        path: 'notifications',
        lazy: async () => {
          const { default: NotificationCenter } = await import('./pages/NotificationCenter');
          return { Component: NotificationCenter };
        },
      },
      {
        path: 'suppliers',
        lazy: async () => {
          const { default: SuppliersList } = await import('./pages/SuppliersList');
          return { Component: SuppliersList };
        },
      },
      {
        path: 'suppliers/new',
        lazy: async () => {
          const { default: SupplierForm } = await import('./pages/SupplierForm');
          return { Component: SupplierForm };
        },
      },
      {
        path: 'suppliers/:id/edit',
        lazy: async () => {
          const { default: SupplierForm } = await import('./pages/SupplierForm');
          return { Component: SupplierForm };
        },
      },
      {
        path: 'suppliers/:id',
        lazy: async () => {
          const { default: SupplierDetail } = await import('./pages/SupplierDetail');
          return { Component: SupplierDetail };
        },
      },
      {
        path: 'locations',
        lazy: async () => {
          const { default: LocationsList } = await import('./pages/LocationsList');
          return { Component: LocationsList };
        },
      },
      {
        path: 'locations/new',
        lazy: async () => {
          const { default: LocationForm } = await import('./pages/LocationForm');
          return { Component: LocationForm };
        },
      },
      {
        path: 'locations/:id/edit',
        lazy: async () => {
          const { default: LocationForm } = await import('./pages/LocationForm');
          return { Component: LocationForm };
        },
      },
      {
        path: 'locations/:id',
        lazy: async () => {
          const { default: LocationDetail } = await import('./pages/LocationDetail');
          return { Component: LocationDetail };
        },
      },
      {
        path: 'categories',
        lazy: async () => {
          const { default: EquipmentCategoriesList } = await import('./pages/EquipmentCategoriesList');
          return { Component: EquipmentCategoriesList };
        },
      },
      {
        path: 'categories/new',
        lazy: async () => {
          const { default: CategoryForm } = await import('./pages/CategoryForm');
          return { Component: CategoryForm };
        },
      },
      {
        path: 'categories/:id/edit',
        lazy: async () => {
          const { default: CategoryForm } = await import('./pages/CategoryForm');
          return { Component: CategoryForm };
        },
      },
      {
        path: 'categories/:id',
        lazy: async () => {
          const { default: CategoryDetail } = await import('./pages/CategoryDetail');
          return { Component: CategoryDetail };
        },
      },
      {
        path: 'stock-movements',
        lazy: async () => {
          const { default: StockMovementsList } = await import('./pages/StockMovementsList');
          return { Component: StockMovementsList };
        },
      },
      {
        path: 'users',
        lazy: async () => {
          const { default: Users } = await import('./pages/Users');
          return { Component: Users };
        },
      },
      {
        path: 'users/:id',
        lazy: async () => {
          const { default: UserForm } = await import('./pages/UserForm');
          return { Component: UserForm };
        },
      },
      {
        path: 'activity-logs',
        lazy: async () => {
          const { default: ActivityLogs } = await import('./pages/ActivityLogs');
          return { Component: ActivityLogs };
        },
      },
      {
        path: 'activity-logs/:id',
        lazy: async () => {
          const { default: ActivityLogDetail } = await import('./pages/ActivityLogDetail');
          return { Component: ActivityLogDetail };
        },
      },
      {
        path: 'documentation',
        lazy: async () => {
          const { default: Documentation } = await import('./pages/Documentation');
          return { Component: Documentation };
        },
      },
      {
        path: 'analytics',
        lazy: async () => {
          const { default: Analytics } = await import('./pages/Analytics');
          return { Component: Analytics };
        },
      },
      {
        path: 'reports',
        lazy: async () => {
          const { default: Reports } = await import('./pages/Reports');
          return { Component: Reports };
        },
      },
      {
        path: 'form-builder',
        lazy: async () => {
          const { default: FormBuilder } = await import('./pages/FormBuilder');
          return { Component: FormBuilder };
        },
      },
      {
        path: 'messages',
        lazy: async () => {
          const { default: ConversationsList } = await import('./pages/ConversationsList');
          return { Component: ConversationsList };
        },
      },
      {
        path: 'messages/:id',
        lazy: async () => {
          const { default: ConversationDetail } = await import('./pages/ConversationDetail');
          return { Component: ConversationDetail };
        },
      },
      {
        path: 'profile-settings',
        lazy: async () => {
          const { default: ProfileSettings } = await import('./pages/ProfileSettings');
          return { Component: ProfileSettings };
        },
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
